import { Button } from '@components/Button';
import PropertyListingCardSkeleton from '@components/Card/PropertyListingCard/SkeletonVersion';
import PropertyCard from '@components/PropertiesSearch/PropertyCard';
import { propertyTypesToBuy } from '@components/PropertiesSearch/utils';
import Block from '@components/Layout/Block';
import { VerticalLineSeparator } from '@components/MiscComponents/VerticalLineSeparator';
import { ListingSortKeys, SortDir } from '@engelvoelkersamericas/api';
import { geocodeCurrentLocation, getGeoLocationRadius } from '@util/location';
import { apiClient } from 'api-client';
import { useTranslations } from 'next-intl';
import { FC, ReactNode, useEffect, useState } from 'react';
import CardGridWithMobileCarousel from '..';
import { CarouselCtaWrapper, CarouselHeaderWrapper, CarouselHeadline } from '../style';

export interface NearbyListingsProps {
  localeDictionary: Record<string, string>;
  locale: string;
}

const NearbyListings: FC<NearbyListingsProps> = () => {
  const t = useTranslations();
  const cardElementsSkeleton = Array(6)
    .fill(null)
    .map((card, ind) => {
      return <PropertyListingCardSkeleton key={ind.toString()} id={ind.toString()} />;
    });

  const [cardArr, setCardArr] = useState<ReactNode[]>(cardElementsSkeleton);
  const [shortLocationTitleForHeader, setShortLocationTitleForHeader] = useState('Nearby Homes');
  const [searchUrl, setSearchUrl] = useState('/properties?listingTypes=For+Sale`');
  const [hideComponent, setHideComponent] = useState(false);

  useEffect(() => {
    geocodeCurrentLocation()
      .then((results) => {
        const geocode = results[0];
        if (!geocode) {
          return;
        }
        //pass geocode lat and lng to get properties
        const coords = geocode.geometry.location;
        getProperties(coords.lat(), coords.lng());

        // set location title for header
        setShortLocationTitleForHeader(shortLocationTitle(geocode));
        // create location string for search url with only city, state, zip and country with commas between
        let location = '';
        if (geocode?.address_components) {
          const city = geocode?.address_components?.find((component) =>
            component.types.includes('locality'),
          );
          const zip = geocode?.address_components?.find((component) =>
            component.types.includes('postal_code'),
          );
          const state = geocode?.address_components?.find((component) =>
            component.types.includes('administrative_area_level_1'),
          );
          const country = geocode?.address_components?.find((component) =>
            component.types.includes('country'),
          );
          location = `${city?.long_name}, ${state?.short_name}, ${zip?.long_name}, ${country?.short_name}`;
        }
        if (location) {
          setSearchUrl(`/properties?listingTypes=For+Sale&location=${location}`);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);

  const getProperties = async (lat: number, lng: number) => {
    let results;
    let radius = 25;

    let polygonData = getGeoLocationRadius(lat, lng, radius);

    try {
      results = await apiClient.getListings({
        page: 1,
        size: 6,
        sortKey: ListingSortKeys.EvaId,
        sortDir: SortDir.Desc,
        listingFilters: {
          geoPolygons: polygonData,
          propertyTypes: propertyTypesToBuy,
        },
      });

      if (results?.data?.listings?.length === 0 && radius === 25) {
        radius = 30;
        polygonData = getGeoLocationRadius(lat, lng, radius);

        results = await apiClient.getListings({
          page: 1,
          size: 6,
          sortKey: ListingSortKeys.EvaId,
          sortDir: SortDir.Desc,
          listingFilters: { geoPolygons: polygonData },
        });
      }

      const mappedToComponents = mapToJsx(results?.data?.listings);
      setCardArr(mappedToComponents);
    } catch (error) {
      console.error(error);
      setHideComponent(true);
    }
  };

  const shortLocationTitle = (geocode) => {
    // create a string of the city and state
    let cityState = '';
    if (geocode?.address_components) {
      const city = geocode?.address_components?.find((component) =>
        component.types.includes('locality'),
      );
      const state = geocode?.address_components?.find((component) =>
        component.types.includes('administrative_area_level_1'),
      );
      cityState = `${t('Homes near')} ${city?.long_name}, ${state?.short_name}`;
    }
    return cityState;
  };

  const mapToJsx = (cardData: any[]): ReactNode[] => {
    // map over cardArr and create array of Property Listing components
    if (cardData && cardData.length > 0) {
      return cardData.map((card: any, ind) => (
        <PropertyCard listing={card} key={ind.toString()} idx={ind} />
      ));
    }
    return [];
  };

  if (hideComponent) {
    // if there is an error in getting properties, don't display the component
    return null;
  }

  return (
    <Block>
      <CarouselHeaderWrapper>
        <CarouselHeadline>{shortLocationTitleForHeader}</CarouselHeadline>
        <Button
          url={'/properties'}
          label={t('Search New Location')}
          variant="tertiaryGray"
          arrow={false}
          icon={'Magnifier'}
          iconPosition={'right'}
        />
      </CarouselHeaderWrapper>

      <VerticalLineSeparator />

      <CardGridWithMobileCarousel cardType="property" cardArr={cardArr} />

      {cardArr && cardArr.length > 0 && (
        <CarouselCtaWrapper>
          <Button
            url={searchUrl}
            dataTestId="view-all-listings-btn"
            label={t('View All Listings')}
            variant="secondary"
            arrow={false}
          />
        </CarouselCtaWrapper>
      )}
    </Block>
  );
};

export default NearbyListings;
